import { API_ENDPOINTS } from '../config/api';
import { authService } from './auth.service';

export interface FavoriteTraining {
  id: string;
  trainingId: string;
  userId: string;
  createdAt: string;
  training: {
    id: string;
    title: string;
    description: string;
    price: number;
    scheduleDate: string;
    duration: string;
    format: string;
    banner_image?: string;
    training_center?: {
      name: string;
      location: string;
    };
    specialties?: string[];
    rating?: number;
  };
}

class FavoritesService {
  private baseUrl = API_ENDPOINTS.BASE_URL;

  // Get user's favorite trainings
  async getFavorites(): Promise<FavoriteTraining[]> {
    if (!authService.isAuthenticated()) {
      throw new Error('User must be authenticated to access favorites');
    }

    try {
      const response = await fetch(`${this.baseUrl}/favorites`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch favorites');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Add training to favorites
  async addToFavorites(trainingId: string): Promise<void> {
    if (!authService.isAuthenticated()) {
      throw new Error('User must be authenticated to add favorites');
    }

    try {
      const response = await fetch(`${this.baseUrl}/favorites`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trainingId }),
      });

      if (!response.ok) {
        throw new Error('Failed to add to favorites');
      }
    } catch (error) {
      throw error;
    }
  }

  // Remove training from favorites
  async removeFromFavorites(trainingId: string): Promise<void> {
    if (!authService.isAuthenticated()) {
      throw new Error('User must be authenticated to remove favorites');
    }

    try {
      const response = await fetch(`${this.baseUrl}/favorites/${trainingId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to remove from favorites');
      }
    } catch (error) {
      throw error;
    }
  }

  // Toggle favorite status
  async toggleFavorite(trainingId: string): Promise<boolean> {
    try {
      const favorites = await this.getFavorites();
      const isFavorite = favorites.some(fav => fav.trainingId === trainingId);
      
      if (isFavorite) {
        await this.removeFromFavorites(trainingId);
        return false;
      } else {
        await this.addToFavorites(trainingId);
        return true;
      }
    } catch (error) {
      throw error;
    }
  }

  // Check if training is favorited
  async isFavorite(trainingId: string): Promise<boolean> {
    if (!authService.isAuthenticated()) {
      return false;
    }

    try {
      const favorites = await this.getFavorites();
      return favorites.some(fav => fav.trainingId === trainingId);
    } catch (error) {
      return false;
    }
  }
}

export const favoritesService = new FavoritesService();
